import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MetricCellView, MetricItemView, MetricsView } from '../../types/agent';

/** The key dates and indicators of a model (sculpt-api PLAN-pf-skills.md §7, `model-summary`), folded behind one
 *  button under the model's summary until the user opens it: the summary gives the figures that matter, the card
 *  is everything `metrics` found. Opened: the key dates, the debt cover, the returns, the financing — each with its
 *  value and a link selecting it, a line's periods and whether its minimum and average were computed, the copies
 *  and the lines computed again as links — then the thresholds, the selectors and what was not found. Drawn from
 *  the `metrics` tool's card, so the list is complete and costs no tokens. */
export function MetricsCard({ view, onShowCell }: {
  view: MetricsView;
  onShowCell?: (sheet: string, cell: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <div className="animate-pc-in-enter overflow-hidden rounded-lg border border-line-card bg-white" data-testid="metrics-card">
        <button type="button" data-testid="metrics-open" onClick={() => setOpen(true)}
          className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[12px] text-ink hover:bg-hover">
          <span className="text-muted">▸</span>
          <span>{t('metrics.show', { indicators: view.counts.indicators, dates: view.counts.dates })}</span>
        </button>
      </div>
    );
  }
  return (
    <div className="animate-pc-in-enter overflow-hidden rounded-lg border border-line-card bg-white" data-testid="metrics-card">
      <div className="border-b border-line-row px-3 py-2">
        <button type="button" data-testid="metrics-close" onClick={() => setOpen(false)}
          className="flex w-full items-center gap-1.5 text-left text-[12.5px] font-medium text-ink">
          <span className="text-muted">▾</span>
          <span>{t('metrics.title')}</span>
        </button>
      </div>

      {view.families.map((f) => (
        <Section key={f.key} title={t(`metrics.family.${f.key}`)} testId={`metrics-family-${f.key}`}>
          {f.items.map((i) => <Item key={`${i.key}-${i.at}`} item={i} onShowCell={onShowCell} />)}
        </Section>
      ))}

      {view.thresholds.length > 0 && (
        <Section title={t('metrics.thresholds')} note={t('metrics.thresholdsNote')} testId="metrics-thresholds">
          {view.thresholds.map((g) => g.items.map((c) => (
            <Row key={`${g.key}-${c.at}`} name={`${g.name} · ${c.label}`} value={c.value ?? ''} cell={c} onShowCell={onShowCell} />
          )))}
        </Section>
      )}

      {view.selectors.length > 0 && (
        <Section title={t('metrics.selectors')} testId="metrics-selectors">
          {view.selectors.map((s) => (
            <Row key={s.label_address} name={s.label} value={s.value ?? '—'} cell={s} onShowCell={onShowCell} />
          ))}
        </Section>
      )}

      {view.missing.length > 0 && (
        <div className="border-t border-line-row px-3 py-1.5 text-[11px] text-muted" data-testid="metrics-missing">
          {t('metrics.missing', { names: view.missing.join(', ') })}
        </div>
      )}
    </div>
  );
}

function Section({ title, note, testId, children }: { title: string; note?: string; testId?: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-line-row py-1 first:border-t-0" data-testid={testId}>
      <div className="px-3 pt-1 text-[11.5px] font-medium text-ink">{title}</div>
      {note && <div className="px-3 text-[11px] text-muted">{note}</div>}
      {children}
    </div>
  );
}

/** One indicator: its value (a line's minimum and average), then, muted, its periods and the other places it
 *  sits — copies on a dashboard, the same line computed again — each a link. */
function Item({ item, onShowCell }: { item: MetricItemView; onShowCell?: (sheet: string, cell: string) => void }) {
  const { t } = useTranslation();
  const s = item.stats;
  const value = s
    ? (s.periods ? t('metrics.minAvg', { min: s.min, avg: s.avg }) : t('metrics.zero'))
    : `${item.value ?? '—'}${item.end?.value ? ` → ${item.end.value}` : ''}`;
  const others = [...item.same_values, ...item.also, ...item.copies];
  const name = item.variant ? `${item.title} · ${item.variant}` : item.title;
  return (
    <div data-testid="metrics-item">
      <Row name={name} value={value} cell={item} onShowCell={onShowCell} />
      {s && s.periods > 0 && (
        <div className="pl-5 pr-3 text-[10.5px] text-muted" data-testid="metrics-periods">
          {t('metrics.periods', { count: s.periods, first: s.first ?? '?', last: s.last ?? '?' })}
          {s.computed && ` · ${t('metrics.computed')}`}
        </div>
      )}
      {others.length > 0 && (
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 pl-5 pr-3 text-[10.5px] text-muted" data-testid="metrics-also">
          <span>{t('metrics.also')}</span>
          {others.slice(0, 4).map((c) => <Place key={c.at} cell={c} onSelect={onShowCell} />)}
          {others.length > 4 && <span>{t('metrics.more', { count: others.length - 4 })}</span>}
        </div>
      )}
    </div>
  );
}

function Row({ name, value, cell, onShowCell }: {
  name: string; value: string; cell: MetricCellView; onShowCell?: (sheet: string, cell: string) => void;
}) {
  return (
    <div className="flex min-w-0 items-baseline gap-1.5 py-0.5 pl-3 pr-3 text-[11.5px]" data-testid="metrics-row">
      <span className="min-w-0 flex-1 truncate text-ink" title={name}>{name}</span>
      <span className="shrink-0 font-mono text-[11px] text-ink" title={value}>{value.length > 28 ? `${value.slice(0, 27)}…` : value}</span>
      <Place cell={cell} onSelect={onShowCell} />
    </div>
  );
}

function Place({ cell, onSelect }: { cell: MetricCellView; onSelect?: (sheet: string, address: string) => void }) {
  if (!cell.address) return null;
  const label = `${cell.sheet}!${cell.address}`;
  if (!onSelect) return <span className="max-w-[40%] shrink-0 truncate font-mono text-[10.5px] text-muted" title={label}>{label}</span>;
  return (
    <button type="button" data-testid="metrics-place" onClick={() => onSelect(cell.sheet, cell.address as string)} title={label}
      className="max-w-[40%] shrink-0 truncate font-mono text-[10.5px] text-accent underline-offset-2 hover:underline">
      {label}
    </button>
  );
}
